import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Modal, Input, Space, Tag, Typography, message, Select, DatePicker, Collapse } from 'antd';
import axios from 'axios';
import moment from 'moment';
import { getBaseUrl } from '../../config';
import '../../layout/DashboardShell.css';
import './FreightDokOpenBoard.css';

const { Option } = Select;
const { Panel } = Collapse;
const { RangePicker } = DatePicker;

const { Text } = Typography;

const formatLocation = (location) => {
  if (!location) return '—';
  const { city, state, zip } = location;
  const cityState = [city, state].filter(Boolean).join(', ');
  return [cityState, zip].filter(Boolean).join(' ');
};

const formatDate = (date) => {
  if (!date) return 'TBD';
  const m = moment(date);
  return m.isValid() ? m.format('MMM DD, YYYY') : 'TBD';
};

const formatDateTime = (date) => {
  if (!date) return '—';
  const m = moment(date);
  return m.isValid() ? m.format('MMM DD, YYYY hh:mm A') : '—';
};

const formatBoardDate = (date) => {
  if (!date) return 'TBD';
  const m = moment(date);
  return m.isValid() ? m.format('MMM DD, HH:mm') : 'TBD';
};

const getBrokerName = (record) => {
  if (!record) return 'Broker';
  if (typeof record.broker === 'string') return record.broker;
  return record.broker?.name
    || record.brokerName
    || record.brokerCompany
    || record.customer
    || record.customerName
    || 'Broker';
};

const getRateValue = (record) => {
  const value = record?.bookNowRate ?? record?.rate ?? record?.price ?? record?.priceRange;
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const numeric = Number(value.replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? numeric : null;
  }
  return null;
};

const formatMoney = (value) => {
  if (!Number.isFinite(value)) return 'Negotiable';
  return value.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
};

const getMiles = (record) => {
  const value = record?.miles ?? record?.distanceMiles ?? record?.distance ?? record?.routeMiles;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const getEquipment = (record) => record?.equipment || record?.equipmentType || record?.trailerType || 'Dry Van';

const getWeight = (record) => {
  const value = record?.weight ?? record?.weightLbs ?? record?.loadWeight;
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? `${numeric.toLocaleString()} lb` : null;
};

const getBidCount = (record) => {
  const value = record?.bidCount ?? record?.bidsCount ?? record?.bids?.length ?? record?.totalBids;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : (record?.myBid ? 1 : 0);
};

const getLocationSearchText = (record) => [
  record?.loadNumber,
  formatLocation(record?.origin || record?.pickup),
  formatLocation(record?.dest || record?.drop),
  getBrokerName(record),
  getEquipment(record)
].filter(Boolean).join(' ').toLowerCase();

const FreightDokOpenBoard = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });
  const [selectedLoad, setSelectedLoad] = useState(null);
  const [bidModalVisible, setBidModalVisible] = useState(false);
  const [bidAmount, setBidAmount] = useState('');
  const [bidTerms, setBidTerms] = useState('');
  const [bidNotes, setBidNotes] = useState('');
  const [modalMode, setModalMode] = useState('bid');
  const [bookNowSubmitting, setBookNowSubmitting] = useState(false);
  const [finalBidAmount, setFinalBidAmount] = useState('');
  const [finalBidMessage, setFinalBidMessage] = useState('');
  const [counterSubmitting, setCounterSubmitting] = useState(false);
  const [finalSubmitting, setFinalSubmitting] = useState(false);
  const [withdrawSubmitting, setWithdrawSubmitting] = useState(false);
  const [boardSearch, setBoardSearch] = useState('');
  const [equipmentFilter, setEquipmentFilter] = useState('all');
  const [originState, setOriginState] = useState('');
  const [destState, setDestState] = useState('');
  const [pickupDateRange, setPickupDateRange] = useState([null, null]);
  const [minRate, setMinRate] = useState('');
  const [maxRate, setMaxRate] = useState('');
  const [sortKey, setSortKey] = useState('rpm');
  const [savedLanes, setSavedLanes] = useState([]);
  const [selectedLaneId, setSelectedLaneId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const pollRef = useRef(null);
  const paginationRef = useRef({ current: 1, pageSize: 10 });
  const filterRef = useRef({
    boardSearch, equipmentFilter, originState, destState,
    pickupDateRange, minRate, maxRate
  });

  useEffect(() => {
    filterRef.current = {
      boardSearch, equipmentFilter, originState, destState,
      pickupDateRange, minRate, maxRate
    };
  }, [boardSearch, equipmentFilter, originState, destState, pickupDateRange, minRate, maxRate]);

  const fetchSavedLanes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/lane-preferences`, {
        headers: { 'x-auth-token': token }
      });
      if (response.data.success) {
        const lanes = response.data.preferences || [];
        setSavedLanes(lanes);
        const defaultLane = lanes.find((l) => l.isDefault);
        if (defaultLane && !selectedLaneId) {
          applySavedLane(defaultLane);
        }
      }
    } catch (error) {
      console.error('Failed to fetch saved lanes:', error);
    }
  };

  const applySavedLane = (lane) => {
    if (!lane) return;
    setSelectedLaneId(lane._id);
    setOriginState(lane.originStates?.[0] || '');
    setDestState(lane.destStates?.[0] || '');
    setEquipmentFilter(lane.equipment?.[0] || 'all');
    setMinRate(lane.minRate != null ? String(lane.minRate) : '');
    setMaxRate(lane.maxRate != null ? String(lane.maxRate) : '');
  };

  const saveCurrentLane = async () => {
    const label = window.prompt('Name this saved lane (e.g. "OH -> IN Dry Van")');
    if (!label) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/lane-preferences`, {
        label,
        originStates: originState ? [originState] : [],
        destStates: destState ? [destState] : [],
        equipment: equipmentFilter && equipmentFilter !== 'all' ? [equipmentFilter] : [],
        minRate: minRate ? Number(minRate) : null,
        maxRate: maxRate ? Number(maxRate) : null
      }, {
        headers: { 'x-auth-token': token }
      });
      if (response.data.success) {
        message.success('Lane saved');
        fetchSavedLanes();
      }
    } catch (error) {
      console.error('Failed to save lane:', error);
      message.error(error.response?.data?.message || 'Failed to save lane');
    }
  };

  const deleteSavedLane = async (lane) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/lane-preferences/${lane._id}`, {
        headers: { 'x-auth-token': token }
      });
      message.success('Lane removed');
      setSavedLanes((prev) => prev.filter((l) => l._id !== lane._id));
      if (selectedLaneId === lane._id) {
        setSelectedLaneId(null);
      }
    } catch (error) {
      console.error('Failed to delete lane:', error);
      message.error('Failed to delete lane');
    }
  };

  const clearFilters = () => {
    setBoardSearch('');
    setEquipmentFilter('all');
    setOriginState('');
    setDestState('');
    setPickupDateRange([null, null]);
    setMinRate('');
    setMaxRate('');
    setSelectedLaneId(null);
  };

  const buildFilterParams = (page = 1, limit = 10) => {
    const params = { page, limit };
    if (boardSearch) params.search = boardSearch;
    if (equipmentFilter && equipmentFilter !== 'all') params.equipment = equipmentFilter;
    if (originState) params.originState = originState.toUpperCase();
    if (destState) params.destState = destState.toUpperCase();
    if (pickupDateRange?.[0]) params.pickupDateFrom = pickupDateRange[0].format('YYYY-MM-DD');
    if (pickupDateRange?.[1]) params.pickupDateTo = pickupDateRange[1].format('YYYY-MM-DD');
    if (minRate) params.minRate = minRate;
    if (maxRate) params.maxRate = maxRate;
    return params;
  };

  const fetchLoads = async (page = 1, limit = 10) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = buildFilterParams(page, limit);
      const response = await axios.get(`${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads`, {
        params,
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        setLoads(response.data.loads || []);
        setPagination({
          current: response.data.pagination.page,
          pageSize: response.data.pagination.limit,
          total: response.data.pagination.total
        });
        paginationRef.current = {
          current: response.data.pagination.page,
          pageSize: response.data.pagination.limit
        };
      }
    } catch (error) {
      console.error('Failed to fetch marketplace loads:', error);
      if (error.response) {
        message.error(error.response.data?.message || 'Failed to fetch loads');
      } else if (error.request) {
        message.error('Network error: please make sure the backend is reachable.');
      } else {
        message.error('Failed to fetch loads');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSavedLanes();
    fetchLoads();

    // Support deep links (e.g. from marketplace emails)
    const urlParams = new URLSearchParams(window.location.search);
    const loadIdParam = urlParams.get('load');

    if (loadIdParam) {
      // Give the list a moment to load before opening the modal
      setTimeout(() => {
        handleViewDetails(loadIdParam);
      }, 1200);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when filters change (debounced slightly)
  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination((prev) => ({ ...prev, current: 1 }));
      paginationRef.current = { current: 1, pageSize: pagination.pageSize };
      fetchLoads(1, pagination.pageSize);
    }, 400);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardSearch, equipmentFilter, originState, destState, pickupDateRange, minRate, maxRate]);

  // Visibility/focus-aware polling to keep board fresh without manual refresh
  useEffect(() => {
    const stopPolling = () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };

    const startPolling = () => {
      stopPolling();
      if (!document.hidden) {
        pollRef.current = setInterval(() => {
          const { current, pageSize } = paginationRef.current;
          fetchLoads(current, pageSize);
        }, 25000); // 25s interval while visible
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        const { current, pageSize } = paginationRef.current;
        fetchLoads(current, pageSize); // immediate refresh on focus/visibility
        startPolling();
      }
    };

    const handleFocus = () => {
      const { current, pageSize } = paginationRef.current;
      fetchLoads(current, pageSize);
      startPolling();
    };

    handleVisibility(); // kick off on mount
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleFocus);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTableChange = ({ current, pageSize }) => {
    fetchLoads(current, pageSize);
  };

  const resetModalState = () => {
    setBidModalVisible(false);
    setSelectedLoad(null);
    setBidAmount('');
    setBidTerms('');
    setBidNotes('');
    setModalMode('bid');
    setBookNowSubmitting(false);
    setFinalBidAmount('');
    setFinalBidMessage('');
    setCounterSubmitting(false);
    setFinalSubmitting(false);
    setWithdrawSubmitting(false);
  };

  const formatCountdown = (expiresAt) => {
    if (!expiresAt) return null;
    const remaining = moment(expiresAt).diff(moment(), 'seconds');
    if (remaining <= 0) return 'Expired';
    const minutes = Math.floor(remaining / 60);
    const seconds = remaining % 60;
    return `${minutes}:${String(seconds).padStart(2, '0')}`;
  };

  const handleViewDetails = async (loadId, mode = 'auto') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${loadId}`, {
        headers: { 'x-auth-token': token }
      });

      if (response.data.success) {
        const load = response.data.load;
        const resolvedMode = mode === 'auto'
          ? (load.bookNow ? 'book_now' : 'bid')
          : mode;
        setSelectedLoad(load);
        setModalMode(resolvedMode);
        setBidModalVisible(true);
        if (resolvedMode === 'bid' && load.myBid) {
          setBidAmount(load.myBid.amount.toString());
          setBidTerms(load.myBid.terms || '');
          setBidNotes(load.myBid.notes || '');
        }
      }
    } catch (error) {
      console.error('Failed to fetch load details:', error);
      message.error('Failed to fetch load details');
    }
  };

  const handleBidSubmit = async () => {
    if (!bidAmount) {
      message.error('Please enter a bid amount');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${selectedLoad.loadId}/bid`,
        {
          bidAmount: parseFloat(bidAmount),
          terms: bidTerms,
          notes: bidNotes
        },
        { headers: { 'x-auth-token': token } }
      );

      if (response.data.success) {
        message.success('Bid submitted successfully');
        resetModalState();
        fetchLoads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to place bid:', error);
      message.error(error.response?.data?.message || 'Failed to place bid');
    }
  };

  const handleBookNowConfirm = async () => {
    if (!selectedLoad?.loadId) return;
    try {
      setBookNowSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${selectedLoad.loadId}/book-now`,
        {},
        { headers: { 'x-auth-token': token } }
      );

      if (response.data.success) {
        message.success('Load booked successfully');
        resetModalState();
        fetchLoads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to book load:', error);
      message.error(error.response?.data?.message || 'Failed to book load');
    } finally {
      setBookNowSubmitting(false);
    }
  };

  const handleAcceptCounter = async () => {
    if (!selectedLoad?.loadId || !selectedLoad?.myBid?.bidId) return;
    try {
      setCounterSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${selectedLoad.loadId}/bids/${selectedLoad.myBid.bidId}/accept-counter`,
        {},
        { headers: { 'x-auth-token': token } }
      );
      if (response.data.success) {
        message.success('Counter accepted');
        resetModalState();
        fetchLoads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to accept counter:', error);
      message.error(error.response?.data?.message || 'Failed to accept counter');
    } finally {
      setCounterSubmitting(false);
    }
  };

  const handleSubmitFinalBid = async () => {
    if (!selectedLoad?.loadId || !selectedLoad?.myBid?.bidId) return;
    if (!finalBidAmount) {
      message.error('Please enter your final bid amount');
      return;
    }
    try {
      setFinalSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${selectedLoad.loadId}/bids/${selectedLoad.myBid.bidId}/final`,
        { finalAmount: parseFloat(finalBidAmount), message: finalBidMessage },
        { headers: { 'x-auth-token': token } }
      );
      if (response.data.success) {
        message.success('Final bid submitted');
        resetModalState();
        fetchLoads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to submit final bid:', error);
      message.error(error.response?.data?.message || 'Failed to submit final bid');
    } finally {
      setFinalSubmitting(false);
    }
  };

  const handleWithdrawBid = async () => {
    if (!selectedLoad?.loadId || !selectedLoad?.myBid?.bidId) return;
    try {
      setWithdrawSubmitting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${getBaseUrl().replace(/\/$/, '')}/api/carrier-marketplace/loads/${selectedLoad.loadId}/bids/${selectedLoad.myBid.bidId}/withdraw`,
        {},
        { headers: { 'x-auth-token': token } }
      );
      if (response.data.success) {
        message.success('Bid withdrawn');
        resetModalState();
        fetchLoads(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      console.error('Failed to withdraw bid:', error);
      message.error(error.response?.data?.message || 'Failed to withdraw bid');
    } finally {
      setWithdrawSubmitting(false);
    }
  };

  const stats = useMemo(() => {
    const total = pagination.total || loads.length;
    const withBid = loads.filter((load) => Boolean(load.myBid)).length;
    const openLoads = total - withBid;
    return {
      total,
      withBid,
      open: openLoads < 0 ? 0 : openLoads
    };
  }, [loads, pagination]);

  const visibleLoads = useMemo(() => {
    return [...loads].sort((a, b) => {
      const rateA = getRateValue(a) || 0;
      const rateB = getRateValue(b) || 0;
      const milesA = getMiles(a) || 0;
      const milesB = getMiles(b) || 0;
      const rpmA = milesA ? rateA / milesA : 0;
      const rpmB = milesB ? rateB / milesB : 0;

      if (sortKey === 'rate') return rateB - rateA;
      if (sortKey === 'miles') return milesB - milesA;
      return rpmB - rpmA;
    });
  }, [loads, sortKey]);

  const getPickupDate = (record) => record.pickupDate || record.pickup?.date || record.pickup?.scheduledDate;
  const getDropDate = (record) => record.dropDate || record.drop?.date || record.drop?.scheduledDate;

  const isCountered = modalMode !== 'book_now' && selectedLoad?.myBid?.status === 'countered';
  const totalPages = Math.max(1, Math.ceil((pagination.total || visibleLoads.length || 1) / pagination.pageSize));

  return (
    <div className="fd-page fd-open-board-page">
      <div className="fd-board-heading">
        <div>
          <h1 className="fd-page-title">Open board</h1>
          <p className="fd-page-subtitle">{stats.total} loads match your saved lanes and equipment.</p>
        </div>
        <div className="fd-sort-tabs" aria-label="Sort open board">
          {[
            ['rpm', 'Sort: RPM'],
            ['rate', 'Sort: RATE'],
            ['miles', 'Sort: MILES']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={sortKey === key ? 'active' : ''}
              onClick={() => setSortKey(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="fd-board-toolbar">
        <label className="fd-board-search">
          <span aria-hidden="true">⌕</span>
          <input
            value={boardSearch}
            onChange={(event) => setBoardSearch(event.target.value)}
            placeholder="Search by city, broker, load ID..."
          />
        </label>
        <div className="fd-board-filters" aria-label="Equipment filters">
          {[
            ['all', 'All'],
            ['dry', 'Dry'],
            ['reefer', 'Reefer'],
            ['flatbed', 'Flatbed']
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={equipmentFilter === key ? 'active' : ''}
              onClick={() => setEquipmentFilter(key)}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          className="fd-more-filters"
          type="button"
          onClick={() => setShowFilters((prev) => !prev)}
        >
          {showFilters ? 'Hide filters' : 'More filters'}
        </button>
      </div>

      {showFilters && (
        <div className="fd-board-advanced-filters">
          <div className="fd-filter-row">
            <div className="fd-filter-field">
              <label>Origin State</label>
              <Input
                placeholder="e.g. OH"
                value={originState}
                onChange={(e) => setOriginState(e.target.value)}
                maxLength={2}
                style={{ width: 100 }}
              />
            </div>
            <div className="fd-filter-field">
              <label>Destination State</label>
              <Input
                placeholder="e.g. IN"
                value={destState}
                onChange={(e) => setDestState(e.target.value)}
                maxLength={2}
                style={{ width: 100 }}
              />
            </div>
            <div className="fd-filter-field">
              <label>Pickup Dates</label>
              <RangePicker
                value={pickupDateRange}
                onChange={(dates) => setPickupDateRange(dates || [null, null])}
                format="MM/DD/YYYY"
                style={{ width: 220 }}
              />
            </div>
            <div className="fd-filter-field">
              <label>Min Rate</label>
              <Input
                type="number"
                placeholder="$"
                value={minRate}
                onChange={(e) => setMinRate(e.target.value)}
                style={{ width: 110 }}
              />
            </div>
            <div className="fd-filter-field">
              <label>Max Rate</label>
              <Input
                type="number"
                placeholder="$"
                value={maxRate}
                onChange={(e) => setMaxRate(e.target.value)}
                style={{ width: 110 }}
              />
            </div>
            <div className="fd-filter-field fd-filter-actions">
              <Button type="primary" onClick={saveCurrentLane}>Save Lane</Button>
              <Button onClick={clearFilters}>Clear</Button>
            </div>
          </div>

          {savedLanes.length > 0 && (
            <div className="fd-saved-lanes">
              <span className="fd-saved-lanes-label">Saved lanes:</span>
              {savedLanes.map((lane) => (
                <Tag
                  key={lane._id}
                  color={selectedLaneId === lane._id ? 'blue' : undefined}
                  closable
                  onClick={() => applySavedLane(lane)}
                  onClose={(e) => {
                    e.preventDefault();
                    deleteSavedLane(lane);
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  {lane.label || `${lane.originStates?.join(',') || 'Any'} → ${lane.destStates?.join(',') || 'Any'}`}
                </Tag>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="fd-card fd-board-table-card">
        <div className="fd-board-table-header">
          <span>Route & Equipment</span>
          <span>Pickup / Delivery</span>
          <span>Broker</span>
          <span>Rate</span>
          <span>RPM</span>
          <span>Bids</span>
          <span />
        </div>

        {loading && <div className="fd-board-loading">Loading open board...</div>}

        {!loading && visibleLoads.length === 0 && (
          <div className="fd-board-empty">
            No matching loads found. Try clearing search or filters.
          </div>
        )}

        {!loading && visibleLoads.map((record, index) => {
          const origin = formatLocation(record.origin || record.pickup);
          const destination = formatLocation(record.dest || record.drop);
          const rate = getRateValue(record);
          const miles = getMiles(record);
          const rpm = rate && miles ? rate / miles : null;
          const equipment = getEquipment(record);
          const weight = getWeight(record);
          const bidCount = getBidCount(record);
          const broker = getBrokerName(record);

          return (
            <button
              key={record.loadId || record._id || record.loadNumber || index}
              className="fd-board-row"
              type="button"
              style={{ animationDelay: `${index * 25}ms` }}
              onClick={() => handleViewDetails(record.loadId, record.bookNow ? 'book_now' : 'bid')}
            >
              <span className="fd-route-cell">
                <strong>{origin} <span>→</span> {destination}</strong>
                <small>
                  <span className="fd-mono">{record.loadNumber || record.loadId || 'FD-LOAD'}</span>
                  {miles ? <span>{miles.toLocaleString()} mi</span> : null}
                  <span>{equipment}</span>
                  {weight ? <span>{weight}</span> : null}
                  {record.bookNow ? <em>Book Now</em> : null}
                </small>
              </span>
              <span className="fd-date-cell">
                <strong>{formatBoardDate(getPickupDate(record))}</strong>
                <small>→ {formatBoardDate(getDropDate(record))}</small>
              </span>
              <span className="fd-broker-cell">
                <strong>{broker}</strong>
                <small>☆ {record.brokerRating || record.rating || '4.6'}</small>
              </span>
              <span className="fd-money-cell">{formatMoney(rate)}</span>
              <span className="fd-rpm-cell">{rpm ? `$${rpm.toFixed(2)}` : '—'}</span>
              <span className="fd-bids-cell">{bidCount}</span>
              <span className="fd-row-action">›</span>
            </button>
          );
        })}
      </div>

      <div className="fd-board-footer">
        <span>
          Showing {visibleLoads.length} of {pagination.total || visibleLoads.length} loads
        </span>
        <div>
          <button
            type="button"
            disabled={pagination.current <= 1}
            onClick={() => handleTableChange({ current: pagination.current - 1, pageSize: pagination.pageSize })}
          >
            Previous
          </button>
          <span className="fd-mono">Page {pagination.current} / {totalPages}</span>
          <button
            type="button"
            disabled={pagination.current >= totalPages}
            onClick={() => handleTableChange({ current: pagination.current + 1, pageSize: pagination.pageSize })}
          >
            Next
          </button>
        </div>
      </div>

      <Modal
        title={modalMode === 'book_now' ? 'Confirm Book Now' : (selectedLoad?.myBid ? 'Update Your Bid' : 'Place a Bid')}
        visible={bidModalVisible}
        onCancel={resetModalState}
        destroyOnClose
        footer={
          isCountered
            ? null
            : modalMode === 'book_now'
              ? [
                  <Button key="cancel" onClick={resetModalState}>
                    Cancel
                  </Button>,
                  <Button key="confirm" type="primary" loading={bookNowSubmitting} onClick={handleBookNowConfirm}>
                    Confirm Book Now
                  </Button>
                ]
              : [
                  <Button key="cancel" onClick={resetModalState}>
                    Cancel
                  </Button>,
                  <Button key="submit" type="primary" onClick={handleBidSubmit}>
                    {selectedLoad?.myBid ? 'Update Bid' : 'Submit Bid'}
                  </Button>
                ]
        }
      >
        {selectedLoad && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <h3 className="fd-modal-load-title">{selectedLoad.loadNumber}</h3>
              <Space direction="vertical" size={0}>
                <Text type="secondary">
                  Pickup: {formatLocation(selectedLoad.origin || selectedLoad.pickup)}
                </Text>
                <Text type="secondary">
                  Delivery: {formatLocation(selectedLoad.dest || selectedLoad.drop)}
                </Text>
              </Space>
            </div>

            <Space direction="vertical" size={4}>
              <Text><strong>Pickup Date:</strong> {formatDate(getPickupDate(selectedLoad))}</Text>
              <Text><strong>Delivery Date:</strong> {formatDate(getDropDate(selectedLoad))}</Text>
              <Text><strong>Rate:</strong> {selectedLoad.bookNowRate != null ? `$${selectedLoad.bookNowRate}` : (selectedLoad.priceRange || 'Negotiable')}</Text>
              <Text><strong>Equipment:</strong> {selectedLoad.equipment || '—'}</Text>
              <Text><strong>Expires:</strong> {formatDateTime(selectedLoad.expiresAt)}</Text>
            </Space>

            {modalMode !== 'book_now' && selectedLoad.myBid && (
              <Tag color={selectedLoad.myBid.status === 'countered' ? 'orange' : 'blue'} style={{ alignSelf: 'flex-start' }}>
                Your current bid: ${selectedLoad.myBid.amount} ({selectedLoad.myBid.status})
              </Tag>
            )}

            {modalMode === 'book_now' ? (
              <div style={{ background: '#f6ffed', border: '1px solid #b7eb8f', padding: 12, borderRadius: 6 }}>
                <Text>
                  By confirming, you are committing to cover this load at the listed rate. Please confirm you are available and ready to run this lane.
                </Text>
              </div>
            ) : (
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                {isCountered && (
                  <div style={{ background: '#fff7e6', border: '1px solid #ffd591', padding: 12, borderRadius: 6 }}>
                    <Space direction="vertical" size={6} style={{ width: '100%' }}>
                      <Text strong>Sunny Logistics sent a counter offer</Text>
                      <Text>Your bid: ${selectedLoad.myBid.amount}</Text>
                      <Text>Counter offer: ${selectedLoad.myBid.counterAmount}</Text>
                      <Text type="secondary">⏱ {formatCountdown(selectedLoad.myBid.roundExpiresAt) || '—'} remaining</Text>
                      <Space wrap>
                        <Button type="primary" loading={counterSubmitting} onClick={handleAcceptCounter}>
                          Accept Counter
                        </Button>
                        <Button onClick={handleWithdrawBid} loading={withdrawSubmitting}>
                          Withdraw
                        </Button>
                      </Space>
                      <div>
                        <Text strong>Send Final Bid</Text>
                        <Input
                          type="number"
                          placeholder="Your final bid"
                          value={finalBidAmount}
                          onChange={(e) => setFinalBidAmount(e.target.value)}
                          prefix="$"
                          style={{ marginTop: 8 }}
                        />
                        <Input
                          placeholder="Message (optional)"
                          value={finalBidMessage}
                          onChange={(e) => setFinalBidMessage(e.target.value)}
                          style={{ marginTop: 8 }}
                        />
                        <div style={{ marginTop: 8 }}>
                          <Button type="primary" loading={finalSubmitting} onClick={handleSubmitFinalBid}>
                            Submit Final Bid
                          </Button>
                        </div>
                      </div>
                    </Space>
                  </div>
                )}

                {!isCountered && (
                  <>
                    <div>
                      <Text strong>Bid Amount *</Text>
                      <Input
                        type="number"
                        placeholder="Enter bid amount"
                        value={bidAmount}
                        onChange={(e) => setBidAmount(e.target.value)}
                        prefix="$"
                      />
                    </div>

                    <div>
                      <Text strong>Terms (optional)</Text>
                      <Input
                        placeholder="e.g. Quick Pay, Net 30"
                        value={bidTerms}
                        onChange={(e) => setBidTerms(e.target.value)}
                      />
                    </div>

                    <div>
                      <Text strong>Notes (optional)</Text>
                      <Input.TextArea
                        rows={3}
                        placeholder="Add any details you want the broker to see"
                    value={bidNotes}
                    onChange={(e) => setBidNotes(e.target.value)}
                      />
                    </div>
                  </>
                )}
              </Space>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default FreightDokOpenBoard; 